<script setup lang="ts">
import { computed } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import Button from '../buttons/button/button.vue';
import { ButtonColor, ButtonVariant } from '../buttons/buttons.constants';
import Icon from '../icon/icon.vue';
import { IconName, IconSize } from '../icon/icon.constants';
import Menu from '../menu/menu.vue';
import type { MenuEntry } from '../menu/menu.types';
import { useLabels } from '../../composables/use-labels';
import { useLink } from '../../composables/use-link';
import type { BreadcrumbItem } from './breadcrumbs.types';

type Props = {
  /** Trail from the top section down to the current page, which is the last item. */
  items: BreadcrumbItem[];
};

const props = defineProps<Props>();

const labels = useLabels();
const linkComponent = useLink();

const parents = computed(() => props.items.slice(0, -1));
const current = computed(() => props.items.at(-1));

const menuItems = computed<MenuEntry[]>(() =>
  parents.value.map(({ label, to }) => ({ label, to })),
);

const isCurrent = (index: number) => index === props.items.length - 1;

const linkProps = (to: RouteLocationRaw) =>
  typeof linkComponent === 'string'
    ? { href: to }
    : { to, activeClass: '', exactActiveClass: '' };

const stepClass =
  'flex h-10 min-w-0 max-w-[25rem] items-center rounded-sm px-lg text-body-md outline-none focus-visible:[outline:2px_solid_currentColor] focus-visible:-outline-offset-2';
</script>

<template>
  <nav v-if="current" :aria-label="labels.breadcrumbs">
    <div class="w-full md:hidden">
      <Menu :items="menuItems" match-trigger-width>
        <template #trigger>
          <Button
            :label="current.label"
            :right-icon-name="IconName.CHEVRON_DOWN"
            :variant="ButtonVariant.OUTLINED"
            :color="ButtonColor.SECONDARY"
            block
          />
        </template>
      </Menu>
    </div>
    <ol
      class="hidden max-w-full items-center text-on-surface-primary md:inline-flex"
    >
      <li
        v-for="(item, index) in props.items"
        :key="index"
        class="flex min-w-0 items-center"
      >
        <component
          :is="linkComponent"
          v-bind="linkProps(item.to)"
          :aria-current="isCurrent(index) ? 'page' : undefined"
          :class="[stepClass, { 'text-primary': isCurrent(index) }]"
        >
          <span class="truncate">{{ item.label }}</span>
        </component>
        <Icon
          v-if="!isCurrent(index)"
          :name="IconName.CHEVRON_RIGHT"
          :size="IconSize.SM"
          class="shrink-0 text-on-surface-secondary"
          aria-hidden="true"
        />
      </li>
    </ol>
  </nav>
</template>
